import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchPurchaseOrders,
  fetchPoFiltersData,
  fetchPurchaseOrderDetail,
  setPoFilters,
  resetPoFilters,
  setPoPage,
  toggleSelectedId,
  clearSelectedIds,
  deletePurchaseOrder,
  submitPurchaseOrder,
  unsubmitPurchaseOrder,
  cancelPurchaseOrder,
  splitPurchaseOrder,
  mergePurchaseOrders,
  rejectAtrPurchaseOrder,
  PoListItem,
  PoMinimalStore,
  PoDetail,
} from './poSlice';
import { AppDispatch, RootState } from '../../app/store';
import toast from 'react-hot-toast';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatUtils';
import { getPoRowColor } from '../../utils/statusUtils';

const getPoStatusDisplay = (po: PoListItem): { text: string; bgColor: string; textColor: string } => {
  const statusId = po.status_id;
  const submitted = typeof po.submitted === 'number' ? po.submitted !== 0 : po.submitted;

  if (statusId === 2) return { text: 'Approved', bgColor: 'bg-white/20', textColor: 'text-white' };
  if (statusId === 3) return { text: 'Rejected', bgColor: 'bg-white/20', textColor: 'text-white' };
  if (statusId === 4) return { text: 'Cancelled', bgColor: 'bg-white/20', textColor: 'text-white' };
  if (!!po.reference_number && !po.order_number) return { text: 'TBA', bgColor: 'bg-white/20', textColor: 'text-white' };
  if (submitted) return { text: 'Submitted', bgColor: 'bg-white/20', textColor: 'text-white' };
  return { text: 'Pending', bgColor: 'bg-white/20', textColor: 'text-white' };
};

const STORE_PREVIEW_LIMIT = 3;

const StoreList: React.FC<{ stores: PoMinimalStore[] }> = ({ stores }) => {
  const count = stores?.length || 0;

  if (count === 0) {
    return <span>N/A</span>;
  }

  if (count === 1) {
    return <span>{stores[0].name}</span>;
  }

  const preview = stores.slice(0, STORE_PREVIEW_LIMIT).map((s) => s.name).join(', ');
  const remaining = count - STORE_PREVIEW_LIMIT;
  const fullList = stores.map((s) => s.name).join(', ');

  return (
    <span title={fullList}>
      <strong>({count} stores)</strong> {preview}
      {!!(remaining > 0) && <span> +{remaining} more</span>}
    </span>
  );
};

// SVG icons matching FontAwesome icons from Livewire
const ClockIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);

const PaperPlaneIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
  </svg>
);

const CheckDoubleIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" opacity="0.5" transform="translate(2, 2)" />
  </svg>
);

const TimesIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const BanIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
  </svg>
);

// Cache for PO details to avoid re-fetching
const sharedPoDetailsCache = new Map<number, any[]>();
const poLoadingStates = new Set<number>();

interface PoProcessedItem {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  details?: string;
  descriptions?: string[];
  badgeColor: string;
}

const PoItemsPreview: React.FC<{ poId: number }> = ({ poId }) => {
  const dispatch: AppDispatch = useDispatch();
  const [items, setItems] = useState<PoProcessedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasLoaded) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [hasLoaded]);

  useEffect(() => {
    if (isVisible && !hasLoaded && !loading) {
      const cachedItems = sharedPoDetailsCache.get(poId);
      if (cachedItems) {
        setItems(cachedItems);
        setExpanded(true);
        setShowAll(false);
        setHasLoaded(true);
        return;
      }
      if (poLoadingStates.has(poId)) return;

      setLoading(true);
      poLoadingStates.add(poId);

      dispatch(fetchPurchaseOrderDetail(poId))
        .unwrap()
        .then((response: any) => {
          const po: PoDetail = response.data || response;
          const processedItems: PoProcessedItem[] = [];

        if (po.equipment_items && po.equipment_items.length > 0) {
          po.equipment_items.forEach((item: any, index: number) => {
            const catSub = [item.category, item.subcategory].filter(Boolean).join(' | ');
            processedItems.push({
              id: `po-eq-${index}`,
              type: 'Equipment',
              title: item.description || 'Equipment Item',
              subtitle: catSub ? `Category: ${catSub}` : '',
              details: item.serial_number ? `S/N: ${item.serial_number}` : '',
              descriptions: item.repair_notes ? [item.repair_notes] : undefined,
              badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200',
            });
          });
        }

        if (po.asset_description_pairs && po.asset_description_pairs.length > 0) {
          po.asset_description_pairs.forEach((pair: any, index: number) => {
            processedItems.push({
              id: `po-asset-${index}`,
              type: 'Asset',
              title: `Asset: ${pair.asset}`,
              descriptions: pair.descriptions,
              badgeColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200',
            });
          });
        }

        if (processedItems.length === 0) {
          if (po.equipmentRequests && po.equipmentRequests.length > 0) {
            po.equipmentRequests.forEach((item: any, index: number) => {
              processedItems.push({
                id: `po-eq-legacy-${index}`,
                type: 'Equipment',
                title: item.equipment?.description || 'Equipment Item',
                subtitle: item.equipment?.serial_number ? `S/N: ${item.equipment.serial_number}` : '',
                details: item.notes || '',
                badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200',
              });
            });
          }
          if (po.assets && po.assets.length > 0) {
            po.assets.forEach((item: any, index: number) => {
              const descriptions = item.descriptions?.map((desc: any) => desc.description) || [];
              processedItems.push({
                id: `po-asset-legacy-${index}`,
                type: 'Asset',
                title: `Asset: ${item.asset}`,
                descriptions,
                badgeColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200',
              });
            });
          }
          if (po.repairs && po.repairs.length > 0 && (!po.assets || po.assets.length === 0)) {
            po.repairs.forEach((repair: any, index: number) => {
              processedItems.push({
                id: `po-repair-${index}`,
                type: 'Legacy',
                title: `Item ${index + 1}`,
                descriptions: [repair.description],
                badgeColor: 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200',
              });
            });
          }
        }

        sharedPoDetailsCache.set(poId, processedItems);
        setItems(processedItems);
        setExpanded(true);
        setShowAll(false);
        setHasLoaded(true);
        setLoading(false);
        poLoadingStates.delete(poId);
      })
      .catch(() => {
        setLoading(false);
        setHasLoaded(true);
        poLoadingStates.delete(poId);
      });
    }
  }, [isVisible, hasLoaded, loading, poId, dispatch]);

  return (
    <div className="mt-2" ref={containerRef}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center text-xs font-medium text-white/70 hover:text-white transition-colors"
      >
        <svg
          className={`w-3 h-3 mr-1 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Items to Repair / Request
        {!!loading && <span className="text-xs text-white/50 ml-1">(Loading...)</span>}
        {items.length > 0 && !loading && <span className="ml-1">({items.length})</span>}
      </button>

      {!!expanded && !!hasLoaded && !loading && (
        <div className="mt-1 space-y-1">
          {items.length > 0 ? (
            <>
              {(showAll ? items : items.slice(0, 2)).map((item) => (
                <div key={item.id} className="p-1.5 rounded text-xs bg-white/10">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-white/90">{item.title}</p>
                      {!!item.subtitle && <p className="text-white/60 mt-0.5">{item.subtitle}</p>}
                      {!!item.details && <p className="text-white/60 mt-0.5">{item.details}</p>}
                      {!!item.descriptions && item.descriptions.length > 0 && (
                        <div className="mt-0.5">
                          <p className="text-white/60"><span className="font-medium">Description:</span></p>
                          <ul className="list-disc list-inside ml-1 text-white/60">
                            {item.descriptions.map((desc, dIndex) =>
                              !!desc && <li key={dIndex}>{desc}</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                    <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${item.badgeColor}`}>
                      {item.type.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
              {items.length > 2 && (
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-xs font-medium text-yellow-300 hover:text-yellow-200 transition-colors"
                >
                  {showAll ? 'Show less' : `Show ${items.length - 2} more`}
                </button>
              )}
            </>
          ) : (
            <p className="text-white/50 italic">No items listed for repair or purchase.</p>
          )}
        </div>
      )}
    </div>
  );
};

const PO_FILTERS_STORAGE_KEY = 'po_filters';

const PurchaseOrderListPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const {
    list,
    loading,
    actionLoading,
    error,
    statusCounts,
    filtersData,
    filters,
    currentPage,
    lastPage,
    total,
    perPage,
    selectedIds,
  } = useSelector((state: RootState) => state.purchaseOrders);
  const { user } = useSelector((state: RootState) => state.auth);

  // Local state for searchable dropdowns
  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);
  const [storeDropdownRef, setStoreDropdownRef] = useState<HTMLDivElement | null>(null);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [isSupplierDropdownOpen, setIsSupplierDropdownOpen] = useState(false);
  const [supplierDropdownRef, setSupplierDropdownRef] = useState<HTMLDivElement | null>(null);

  // Load saved filters from localStorage on mount (before first API call)
  const [hasLoadedSavedFilters, setHasLoadedSavedFilters] = useState(false);
  useEffect(() => {
    try {
      const saved = localStorage.getItem(PO_FILTERS_STORAGE_KEY);
      if (saved) {
        const savedFilters = JSON.parse(saved);
        dispatch(setPoFilters(savedFilters));
      }
    } catch (e) {
      console.error('Failed to load saved PO filters:', e);
    }
    setHasLoadedSavedFilters(true);
  }, [dispatch]);

  // Load filters data on mount
  useEffect(() => {
    dispatch(fetchPoFiltersData());
  }, [dispatch]);

  // Load PO list whenever filters change (only after saved filters are loaded)
  useEffect(() => {
    if (!hasLoadedSavedFilters) return;
    dispatch(fetchPurchaseOrders(filters));
  }, [dispatch, filters, hasLoadedSavedFilters]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (!hasLoadedSavedFilters) return;
    try {
      localStorage.setItem(PO_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      console.error('Failed to save PO filters:', e);
    }
  }, [filters, hasLoadedSavedFilters]);

  // Live search with debounce
  const [searchInput, setSearchInput] = useState(filters.search || '');
  useEffect(() => {
    if (!hasLoadedSavedFilters) return;
    const timer = setTimeout(() => {
      if (searchInput !== (filters.search || '')) {
        dispatch(setPoFilters({ search: searchInput || undefined }));
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, hasLoadedSavedFilters, dispatch, filters.search]);

  // Sync searchInput when filters change externally (e.g. reset)
  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (storeDropdownRef && !storeDropdownRef.contains(e.target as Node)) {
        setIsStoreDropdownOpen(false);
      }
      if (supplierDropdownRef && !supplierDropdownRef.contains(e.target as Node)) {
        setIsSupplierDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [storeDropdownRef, supplierDropdownRef]);

  const handleFilterChange = (key: string, value: any) => {
    dispatch(setPoFilters({ [key]: value }));
  };

  const handlePageChange = (page: number) => {
    dispatch(setPoPage(page));
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setStoreSearchTerm('');
    setSupplierSearchTerm('');
    dispatch(resetPoFilters());
  };

  // Store multi-select helpers
  const selectedStoreIds = filters.stores || [];
  const handleStoreToggle = (storeId: number) => {
    const newStores = selectedStoreIds.includes(storeId)
      ? selectedStoreIds.filter(id => id !== storeId)
      : [...selectedStoreIds, storeId];
    dispatch(setPoFilters({ stores: newStores.length > 0 ? newStores : undefined }));
  };
  const filteredStores = (filtersData?.stores || []).filter(s => {
    const term = storeSearchTerm.toLowerCase();
    return s.name.toLowerCase().includes(term) || (s.storenumber || '').toLowerCase().includes(term);
  });

  // Supplier single-select helpers
  const selectedSupplier = (filtersData?.suppliers || []).find(s => s.id === filters.supplier_id);
  const filteredSuppliers = (filtersData?.suppliers || []).filter(s => {
    const term = supplierSearchTerm.toLowerCase();
    return s.name.toLowerCase().includes(term);
  });

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this purchase order? This cannot be undone.')) return;
    try {
      await dispatch(deletePurchaseOrder(id)).unwrap();
      toast.success('Purchase order deleted');
    } catch (err: any) {
      toast.error(err || 'Failed to delete');
    }
  };

  const [showRejectAtrModal, setShowRejectAtrModal] = useState(false);
  const [rejectAtrComment, setRejectAtrComment] = useState('');
  const [showBulkCancelModal, setShowBulkCancelModal] = useState(false);
  const [bulkCancelReason, setBulkCancelReason] = useState('');
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mainOrderId, setMainOrderId] = useState<number | null>(null);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected purchase order(s)? This cannot be undone.`)) return;
    for (const id of selectedIds) {
      try {
        await dispatch(deletePurchaseOrder(id)).unwrap();
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed to delete'}`);
      }
    }
    toast.success('Selected purchase orders deleted');
    dispatch(clearSelectedIds());
  };

  const handleBulkSubmit = async () => {
    if (selectedIds.length === 0 || !canBulkSubmit) return;
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        await dispatch(submitPurchaseOrder(id)).unwrap();
        successCount++;
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed to submit'}`);
      }
    }
    if (successCount > 0) toast.success(`${successCount} order(s) submitted for approval`);
    dispatch(clearSelectedIds());
    dispatch(fetchPurchaseOrders(filters));
  };

  const handleBulkUnsubmit = async () => {
    if (selectedIds.length === 0 || !canBulkUnsubmit) return;
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        await dispatch(unsubmitPurchaseOrder(id)).unwrap();
        successCount++;
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed to unsubmit'}`);
      }
    }
    if (successCount > 0) toast.success(`${successCount} order(s) unsubmitted`);
    dispatch(clearSelectedIds());
    dispatch(fetchPurchaseOrders(filters));
  };

  const handleRejectAtr = async () => {
    if (selectedIds.length === 0 || !canRejectAtr || !rejectAtrComment.trim()) return;
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        await dispatch(rejectAtrPurchaseOrder({ id, comment: rejectAtrComment })).unwrap();
        successCount++;
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed to reject ATR'}`);
      }
    }
    if (successCount > 0) toast.success(`${successCount} ATR(s) rejected`);
    setShowRejectAtrModal(false);
    setRejectAtrComment('');
    dispatch(clearSelectedIds());
    dispatch(fetchPurchaseOrders(filters));
  };

  const handleBulkCancel = async () => {
    if (selectedIds.length === 0 || !canBulkCancel || !bulkCancelReason.trim()) return;
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        await dispatch(cancelPurchaseOrder({ id, reason: bulkCancelReason })).unwrap();
        successCount++;
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed to cancel'}`);
      }
    }
    if (successCount > 0) toast.success(`${successCount} order(s) cancelled`);
    setShowBulkCancelModal(false);
    setBulkCancelReason('');
    dispatch(clearSelectedIds());
    dispatch(fetchPurchaseOrders(filters));
  };

  const handleBulkSplit = async () => {
    if (!canBulkSplit) return;
    try {
      await dispatch(splitPurchaseOrder(selectedIds[0])).unwrap();
      toast.success('Order split successfully');
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to split');
    }
  };

  const handleBulkMerge = async () => {
    if (!canBulkMerge) return;
    if (!mainOrderId) {
      toast.error('Select a main order to merge into');
      return;
    }
    try {
      await dispatch(mergePurchaseOrders({ mainOrderId, orderIds: selectedIds })).unwrap();
      toast.success('Orders merged successfully');
      setShowMergeModal(false);
      setMainOrderId(null);
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to merge');
    }
  };


  // Compute gating flags from selected POs (mirrors Livewire conditions)
  const selectedPOs = list.filter(po => selectedIds.includes(po.id));
  const selectedPending = selectedPOs.filter(po => po.status_id === 1);
  const selectedSubmitted = selectedPOs.filter(po => {
    const isSubmitted = typeof po.submitted === 'number' ? po.submitted !== 0 : po.submitted;
    return isSubmitted;
  });
  const selectedHasOrderNumber = selectedPOs.filter(po => !!po.order_number);
  const selectedCancelled = selectedPOs.filter(po => po.status_id === 4);
  const allPending = selectedPOs.length > 0 && selectedPOs.every(po => po.status_id === 1);
  const noneCancelled = selectedCancelled.length === 0;
  const canDeleteAny = selectedPOs.some(po => po.can_delete);

  // Gate flags
  const canBulkSubmit = selectedPending.length > 0;
  // Unsubmit: all selected must be submitted, none cancelled, none already approved (no approver with status_id = 2)
  const canBulkUnsubmit = selectedSubmitted.length > 0 &&
    selectedSubmitted.length === selectedPOs.length &&
    noneCancelled &&
    selectedPOs.every(po => !po.approvers?.some(a => a.status_id === 2));
  // Reject ATR: has linked requisition, not approved (no order_number), not cancelled
  const canRejectAtr = selectedPOs.some(po => !!po.requisition?.id) &&
    selectedPOs.every(po => !po.order_number) &&
    noneCancelled;
  const canBulkCancel = selectedHasOrderNumber.length > 0 && canDeleteAny && noneCancelled;
  const canBulkSplit = selectedIds.length === 1 && selectedPOs[0]?.stores?.length > 1 && selectedPOs[0]?.status_id === 1;
  const canBulkMerge = selectedIds.length >= 2 && allPending;
  // Delete: none have order_number AND none have reference_number AND can_delete
  const canBulkDelete = selectedPOs.length > 0 &&
    selectedHasOrderNumber.length === 0 &&
    selectedPOs.every(po => !po.reference_number) &&
    canDeleteAny;

  const hasActiveFilters = !!(
    filters.search || filters.status_id || (filters.stores && filters.stores.length > 0) ||
    filters.supplier_id || filters.technician_id || filters.internal !== undefined ||
    filters.date_from || filters.date_to || filters.linked_only || filters.multi_store ||
    filters.required_grn || filters.required_dn_jbc || filters.view_pending || filters.view_submitted ||
    filters.view_tba || filters.view_approved || filters.view_rejected || filters.view_cancelled
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Purchase Orders</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {total} order{total !== 1 ? 's' : ''} total
          </p>
        </div>
      </div>

      {/* Status Count Cards */}
      {!!statusCounts && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {[
            { label: 'Pending', count: statusCounts.pending, gradient: 'bg-gradient-to-br from-[#667eea] to-[#764ba2]', icon: <ClockIcon />, filterKey: 'view_pending' as const },
            { label: 'Submitted', count: statusCounts.submitted_pending, gradient: 'bg-gradient-to-br from-[#17a2b8] to-[#0dcaf0]', icon: <PaperPlaneIcon />, filterKey: 'view_submitted' as const },
            { label: 'TBA', count: statusCounts.tba, gradient: 'bg-gradient-to-br from-[#26408B] to-[#3d5afe]', icon: <PaperPlaneIcon />, filterKey: 'view_tba' as const },
            { label: 'Approved', count: statusCounts.approved, gradient: 'bg-gradient-to-br from-[#198754] to-[#20c997]', icon: <CheckDoubleIcon />, filterKey: 'view_approved' as const },
            { label: 'Rejected', count: statusCounts.rejected, gradient: 'bg-gradient-to-br from-[#dc3545] to-[#f8719d]', icon: <TimesIcon />, filterKey: 'view_rejected' as const },
            { label: 'Cancelled', count: statusCounts.cancelled, gradient: 'bg-gradient-to-br from-[#4F4F4F] to-[#6c757d]', icon: <BanIcon />, filterKey: 'view_cancelled' as const },
          ].map((stat) => {
            const isActive = !!filters[stat.filterKey];
            return (
              <button
                key={stat.label}
                onClick={() => handleFilterChange(stat.filterKey, isActive ? undefined : true)}
                className={`p-3 rounded-lg shadow-sm hover:shadow-md transition text-left info-card ${stat.gradient} ${isActive ? 'ring-4 ring-yellow-400' : 'ring-0'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-white/70 text-xs flex items-center gap-1">
                    {stat.icon}
                    {stat.label}
                  </span>
                  <span className="text-white font-bold text-3xl">{stat.count}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Search Bar - live search with debounce */}
      <div className="flex gap-2">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search order #, reference #, requestor, supplier, technician, store, GRN, quote, invoice..."
          className="flex-1 px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
        />
        <button
          type="button"
          onClick={handleResetFilters}
          disabled={!hasActiveFilters}
          className="px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm disabled:opacity-40"
        >
          Reset Filters
        </button>
      </div>

      {/* Filters Panel - always visible */}
      <div className="p-4 rounded-lg border bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Stores - searchable multi-select */}
          <div className="relative" ref={setStoreDropdownRef}>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Stores {selectedStoreIds.length > 0 && `(${selectedStoreIds.length} selected)`}
            </label>
            <input
              type="text"
              placeholder="Search stores..."
              value={storeSearchTerm}
              onChange={(e) => setStoreSearchTerm(e.target.value)}
              onFocus={() => setIsStoreDropdownOpen(true)}
              className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            />
            {isStoreDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700 max-h-48 overflow-auto">
                {filteredStores.length > 0 ? (
                  filteredStores.map(store => (
                    <div
                      key={store.id}
                      className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer flex items-center text-sm text-slate-900 dark:text-slate-100"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStoreToggle(store.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedStoreIds.includes(store.id)}
                        readOnly
                        className="mr-2 h-4 w-4 rounded"
                      />
                      {store.name}{store.storenumber && ` (${store.storenumber})`}
                    </div>
                  ))
                ) : (
                  <div className="p-2 text-slate-500 text-sm">No stores found</div>
                )}
              </div>
            )}
            {selectedStoreIds.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {selectedStoreIds.map(storeId => {
                  const store = (filtersData?.stores || []).find(s => s.id === storeId);
                  return store ? (
                    <span
                      key={storeId}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-100"
                    >
                      {store.name}{store.storenumber && ` (${store.storenumber})`}
                      <button
                        type="button"
                        className="ml-1.5 inline-flex text-blue-500 hover:text-blue-700"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleStoreToggle(storeId);
                        }}
                      >
                        &times;
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Supplier - searchable single-select */}
          <div className="relative" ref={setSupplierDropdownRef}>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Supplier</label>
            <input
              type="text"
              placeholder={selectedSupplier ? selectedSupplier.name : 'All Suppliers'}
              value={supplierSearchTerm}
              onChange={(e) => setSupplierSearchTerm(e.target.value)}
              onFocus={() => setIsSupplierDropdownOpen(true)}
              className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            />
            {isSupplierDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700 max-h-48 overflow-auto">
                <div
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer text-sm text-slate-900 dark:text-slate-100"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFilterChange('supplier_id', undefined);
                    setSupplierSearchTerm('');
                    setIsSupplierDropdownOpen(false);
                  }}
                >
                  All Suppliers
                </div>
                {filteredSuppliers.map(s => (
                  <div
                    key={s.id}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer text-sm text-slate-900 dark:text-slate-100"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFilterChange('supplier_id', s.id);
                      setSupplierSearchTerm('');
                      setIsSupplierDropdownOpen(false);
                    }}
                  >
                    {s.name}
                  </div>
                ))}
              </div>
            )}
            {selectedSupplier && !isSupplierDropdownOpen && (
              <div className="mt-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-100">
                  {selectedSupplier.name}
                  <button
                    type="button"
                    className="ml-1.5 inline-flex text-blue-500 hover:text-blue-700"
                    onClick={(e) => {
                      e.preventDefault();
                      handleFilterChange('supplier_id', undefined);
                    }}
                  >
                    &times;
                  </button>
                </span>
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Technician</label>
            <select
              value={filters.technician_id || ''}
              onChange={(e) => handleFilterChange('technician_id', e.target.value ? Number(e.target.value) : undefined)}
              className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            >
              <option value="">All Technicians</option>
              {(filtersData?.technicians || []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Type</label>
            <select
              value={filters.internal ?? ''}
              onChange={(e) => handleFilterChange('internal', e.target.value === '' ? undefined : Number(e.target.value))}
              className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            >
              <option value="">All Types</option>
              <option value="1">Internal</option>
              <option value="2">External</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Status</label>
            <select
              value={filters.status_id || ''}
              onChange={(e) => handleFilterChange('status_id', e.target.value ? Number(e.target.value) : undefined)}
              className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            >
              <option value="">All Statuses</option>
              {(filtersData?.statuses || []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Date From</label>
            <input
              type="date"
              value={filters.date_from || ''}
              onChange={(e) => handleFilterChange('date_from', e.target.value || undefined)}
              className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Date To</label>
            <input
              type="date"
              value={filters.date_to || ''}
              onChange={(e) => handleFilterChange('date_to', e.target.value || undefined)}
              className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            />
          </div>
          <div className="flex items-end flex-wrap gap-4 lg:col-span-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.linked_only || false}
                onChange={(e) => handleFilterChange('linked_only', e.target.checked || undefined)}
                className="rounded"
              />
              Linked Only
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.multi_store || false}
                onChange={(e) => handleFilterChange('multi_store', e.target.checked || undefined)}
                className="rounded"
              />
              Multi-Store
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.required_grn || false}
                onChange={(e) => handleFilterChange('required_grn', e.target.checked || undefined)}
                className="rounded"
              />
              GRN Required
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.required_dn_jbc || false}
                onChange={(e) => handleFilterChange('required_dn_jbc', e.target.checked || undefined)}
                className="rounded"
              />
              DN/JBC Required
            </label>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {!!(selectedIds.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>
          <div className="flex-1" />
          {canBulkSubmit && (
            <button
              onClick={handleBulkSubmit}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-indigo-500 text-white rounded-lg text-sm hover:bg-indigo-600 disabled:opacity-50"
            >
              Submit
            </button>
          )}
          {canBulkUnsubmit && (
            <button
              onClick={handleBulkUnsubmit}
              disabled={actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 disabled:opacity-50"
            >
              Unsubmit
            </button>
          )}
          {canRejectAtr && (
            <button
              onClick={() => setShowRejectAtrModal(true)}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 disabled:opacity-50"
            >
              Reject ATR
            </button>
          )}
          {canBulkCancel && (
            <button
              onClick={() => setShowBulkCancelModal(true)}
              disabled={actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          {canBulkSplit && (
            <button
              onClick={handleBulkSplit}
              disabled={actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 disabled:opacity-50"
              title="Split order with multiple stores"
            >
              Split
            </button>
          )}
          {canBulkMerge && (
            <button
              onClick={() => setShowMergeModal(true)}
              disabled={actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 disabled:opacity-50"
              title="Merge pending orders"
            >
              Merge
            </button>
          )}
          {canBulkDelete && (
            <button
              onClick={handleBulkDelete}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 disabled:opacity-50"
            >
              Delete
            </button>
          )}
          <button
            onClick={() => dispatch(clearSelectedIds())}
            className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Error */}
      {!!error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {!!loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-400"></div>
        </div>
      )}

      {/* List */}
      {!!(!loading && list.length === 0) && (
        <div className="text-center py-12 rounded-lg border bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <p className="text-slate-500 dark:text-slate-400">No purchase orders found.</p>
        </div>
      )}

      {!!(!loading && list.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((po) => {
            const status = getPoStatusDisplay(po);
            const isSelected = selectedIds.includes(po.id);
            const rowBg = getPoRowColor(po);
            const canEdit = !!po.can_edit;
            const canSelect = !!po.can_select;
            const canDelete = !!po.can_delete;
            const isSubmitted = typeof po.submitted === 'number' ? po.submitted !== 0 : po.submitted;
            const labelColor = (isSubmitted && !po.order_number && !po.reference_number) ? 'text-slate-900' : 'text-white/60';
            return (
              <div
                key={po.id}
                style={{ backgroundColor: rowBg }}
                className={`p-4 rounded-lg text-white transition ${isSelected ? 'ring-2 ring-white' : ''}`}
              >
                <div className="flex items-start gap-3">
                  {!!canSelect && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => dispatch(toggleSelectedId(po.id))}
                      className="mt-1 rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/purchase-orders/${po.id}`}
                        className="font-semibold text-lg text-white underline decoration-white/40 hover:decoration-white"
                      >
                        {(po.order_number || po.reference_number) ? '' : 'PO'}
                      </Link>
                      {!!po.reference_number && !po.order_number && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-400/90 text-yellow-900">
                          Ref: {po.reference_number}
                        </span>
                      )}
                      {!!po.order_number && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-400/90 text-yellow-900">
                          #{po.order_number}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.textColor}`}>
                        {status.text}
                      </span>
                      {!!po.is_linked && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                          Linked
                        </span>
                      )}
                      {!!(po.internal === 1) && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                          Internal
                        </span>
                      )}
                      {!!(po.internal === 2) && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                          External
                        </span>
                      )}
                      {!!po.requisition?.id && (
                        <Link
                          to={`/requisitions/${po.requisition.id}`}
                          className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/80 hover:bg-purple-500 text-white transition cursor-pointer"
                          title={`View Requisition #${po.requisition.id}`}
                        >
                          ATR #{po.requisition.id}
                        </Link>
                      )}
                      {!!po.non_vat_display && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-600/80 text-white" title="Non-VAT supplier">
                          Non-VAT
                        </span>
                      )}
                      {!!po.is_amended && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/90 text-amber-900" title="Price amended via GRN variance or amendment approval">
                          Amended
                        </span>
                      )}
                      {po.grn_status === 'not_received' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-600/80 text-white" title="No GRN received yet">
                          Not Received
                        </span>
                      )}
                      {po.grn_status === 'received_fully' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-700/80 text-white" title="All items received">
                          Received Fully
                        </span>
                      )}
                      {po.grn_status === 'partially_received' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-600/80 text-white" title="Some items still outstanding">
                          Partially Received
                        </span>
                      )}
                      {!!po.documents_required && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-600/80 text-white animate-pulse" title={`${po.documents_required} Required`}>
                          {po.documents_required} Required
                        </span>
                      )}
                    </div>

                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      <div>
                        <span className={`${labelColor} text-xs`}>Store:</span>{' '}
                        <span className="font-medium text-yellow-300">
                          <StoreList stores={po.stores || []} />
                        </span>
                      </div>
                      <div>
                        <span className={`${labelColor} text-xs`}>{po.internal === 1 ? 'Technician:' : 'Supplier:'}</span>{' '}
                        <span className="font-medium text-yellow-300">
                          {po.internal === 1 ? (po.technician?.name || 'N/A') : (po.supplier?.name || 'N/A')}
                        </span>
                      </div>
                      <div>
                        <span className={`${labelColor} text-xs`}>Date:</span>{' '}
                        <span className="font-medium">{formatDate(po.date)}</span>
                      </div>
                      <div>
                        <span className={`${labelColor} text-xs`}>Requested by:</span>{' '}
                        <span className="font-medium">{po.user?.name || 'N/A'}</span>
                      </div>
                      <div>
                        <span className={`${labelColor} text-xs`}>Category:</span>{' '}
                        <span className="font-medium text-yellow-300">
                          {po.category?.name || 'N/A'}{po.subCategory?.name ? ` - ${po.subCategory.name}` : ''}
                        </span>
                      </div>
                      <div>
                        <span className={`${labelColor} text-xs`}>Type:</span>{' '}
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-400/90 text-yellow-900">
                          {po.is_repair === 1 ? 'Repair' : 'New Purchase'}
                        </span>
                      </div>
                    </div>

                    {!!po.quotation && (
                      <div className="mt-1 text-sm">
                        <span className={`${labelColor} text-xs`}>Quote:</span>{' '}
                        <span className="font-medium text-yellow-300">{po.quotation.quotation_number} - {formatCurrency(po.quotation.amount)}</span>
                      </div>
                    )}

                    {/* Items to Repair / Request - lazy loaded */}
                    <PoItemsPreview poId={po.id} />

                    <div className="mt-2 flex items-center gap-3 text-xs text-white/60">
                      <span className={labelColor}>Updated:</span> {formatDateTime(po.updated_at)}
                      {!!(po.grns && po.grns.length > 0) && (
                        <span className="px-2 py-0.5 rounded-full bg-yellow-400/90 text-yellow-900 font-medium">GRNs: {po.grns!.length}</span>
                      )}
                    </div>

                    {/* Approval chain and action buttons in same container */}
                    <div className="mt-2 pt-2 border-t border-white/20 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        {!!(po.approvers && po.approvers.length > 0) && (
                          <div>
                            <div className={`text-xs ${labelColor} mb-1`}>Approval Chain:</div>
                            <div className="flex flex-wrap gap-1">
                              {po.approvers.map((approver: any) => {
                                let statusColor = 'bg-white/20';
                                let statusText = 'Pending';
                                let icon = <ClockIcon />;
                                if (approver.status_id === 2) { statusColor = 'bg-green-500/80'; statusText = 'Approved'; icon = <CheckDoubleIcon />; }
                                else if (approver.status_id === 3) { statusColor = 'bg-red-500/80'; statusText = 'Rejected'; icon = <TimesIcon />; }
                                return (
                                  <span
                                    key={approver.id}
                                    className={`px-2 py-0.5 rounded-full text-xs ${statusColor} text-white flex items-center gap-1`}
                                    title={`${approver.user?.name || 'Unknown'} - ${statusText}`}
                                  >
                                    {icon}
                                    {approver.user?.name || 'Unknown'}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 sm:justify-end">
                        <Link
                          to={`/purchase-orders/${po.id}`}
                          className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded text-xs transition"
                        >
                          View
                        </Link>
                        {!!(canEdit && !po.order_number) && (
                          <Link
                            to={`/purchase-orders/${po.id}/edit`}
                            className="px-3 py-1.5 bg-blue-500/80 hover:bg-blue-500 text-white rounded text-xs transition"
                          >
                            Edit
                          </Link>
                        )}
                        {!!canDelete && (
                          <button
                            onClick={() => handleDelete(po.id)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded text-xs transition disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!!(lastPage > 1) && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            Page {currentPage} of {lastPage}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === lastPage}
            className="px-3 py-1.5 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Reject ATR Modal */}
      {showRejectAtrModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">Reject ATR — {selectedIds.length} Purchase Order(s)</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              This will reject the linked ATR (requisition), reset approvers, and set the PO status to rejected.
            </p>
            <textarea
              value={rejectAtrComment}
              onChange={(e) => setRejectAtrComment(e.target.value)}
              placeholder="Reason for ATR rejection (required)"
              rows={4}
              className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleRejectAtr}
                disabled={!rejectAtrComment.trim() || actionLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Yes, Reject ATR'}
              </button>
              <button
                onClick={() => { setShowRejectAtrModal(false); setRejectAtrComment(''); }}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Cancel Modal */}
      {showBulkCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">Cancel {selectedIds.length} Purchase Order(s)</h3>
            <textarea
              value={bulkCancelReason}
              onChange={(e) => setBulkCancelReason(e.target.value)}
              placeholder="Reason for cancellation (required)"
              rows={4}
              className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleBulkCancel}
                disabled={!bulkCancelReason.trim() || actionLoading}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Confirm Cancel'}
              </button>
              <button
                onClick={() => { setShowBulkCancelModal(false); setBulkCancelReason(''); }}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">Merge {selectedIds.length} Purchase Orders</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">Select the main order to merge the others into:</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {list.filter(po => selectedIds.includes(po.id)).map(po => (
                <label key={po.id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50">
                  <input
                    type="radio"
                    name="mainOrder"
                    checked={mainOrderId === po.id}
                    onChange={() => setMainOrderId(po.id)}
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {po.order_number ? `#${po.order_number}` : `#${po.id}`} — {po.stores?.map(s => s.name).join(', ')}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleBulkMerge}
                disabled={!mainOrderId || actionLoading}
                className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
              >
                {actionLoading ? 'Merging...' : 'Merge Orders'}
              </button>
              <button
                onClick={() => { setShowMergeModal(false); setMainOrderId(null); }}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderListPage;
